import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Users2, CheckCircle2, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  name: "",
  email: "",
  password: "",
  company: "",
  phone: "",
  project_ids: [],
};

export default function AdminClients() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/projects"),
      ]);
      setRows(c.data);
      setProjects(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowPassword(false);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name,
      email: c.email,
      password: "",
      company: c.company || "",
      phone: c.phone || "",
      project_ids: c.project_ids || [],
    });
    setShowPassword(false);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!editing && !form.email.trim())) {
      toast.error("Name and email required");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        const body = {
          name: form.name,
          company: form.company,
          phone: form.phone,
          project_ids: form.project_ids,
        };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/clients/${editing}`, body);
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", {
          name: form.name,
          email: form.email,
          password: form.password,
          company: form.company,
          phone: form.phone,
          project_ids: form.project_ids,
        });
        toast.success("Client created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl uppercase tracking-tight">Clients</h1>
          <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-1">
            Read-only viewers tied to one or more projects
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-xl bg-slate-900 text-white hover:bg-slate-700 h-9"
          data-testid="clients-new"
        >
          <Plus className="h-4 w-4 mr-1" /> New client
        </Button>
      </div>

      <div className="glass-panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="bg-white/50 border-b border-slate-200/70">
            <tr>
              {["Name", "Email", "Company", "Projects", "Status", "Actions"].map((h) => (
                <th
                  key={h}
                  className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-neutral-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-neutral-500">
                  No clients yet. Add a client to share project dashboards.
                </td>
              </tr>
            ) : (
              rows.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-slate-200/70 hover:bg-white/50"
                  data-testid={`client-row-${c.id}`}
                >
                  <td className="p-3 font-medium flex items-center gap-2">
                    <Users2 className="h-4 w-4 text-neutral-500" />
                    {c.name}
                  </td>
                  <td className="p-3 font-mono text-xs">{c.email}</td>
                  <td className="p-3">{c.company || "—"}</td>
                  <td className="p-3 font-mono">{(c.project_ids || []).length}</td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                        c.active
                          ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                          : "border-slate-300/70 text-neutral-600"
                      }`}
                    >
                      {c.active && <CheckCircle2 className="h-3 w-3" />}
                      {c.active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-xl"
                        onClick={() => openEdit(c)}
                        data-testid={`client-edit-${c.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-xl text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(c)}
                        data-testid={`client-delete-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-md rounded-xl border-slate-300/70"
          data-testid="client-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit client" : "New client"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            {/* Hidden decoys to defeat browser password autofill leaking the admin's saved password */}
            <input
              type="text"
              name="prevent-autofill-username"
              autoComplete="username"
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <input
              type="password"
              name="prevent-autofill-password"
              autoComplete="new-password"
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  autoComplete="off"
                  className="rounded-xl border-slate-300/70"
                  data-testid="client-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider">Company</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  autoComplete="off"
                  className="rounded-xl border-slate-300/70"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editing}
                required={!editing}
                autoComplete="off"
                className="rounded-xl border-slate-300/70"
                data-testid="client-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider">
                  {editing ? "New password (optional)" : "Password"}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    minLength={editing ? 0 : 6}
                    required={!editing}
                    autoComplete="new-password"
                    spellCheck={false}
                    className="rounded-xl border-slate-300/70 pr-9 font-mono"
                    data-testid="client-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="client-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  Click the eye to verify exactly what will be sent to the client
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  autoComplete="off"
                  className="rounded-xl border-slate-300/70"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">
                Assign projects ({form.project_ids.length} selected)
              </Label>
              <div className="mt-2 border border-slate-300/60">
                <div className="max-h-44 overflow-y-auto" data-testid="client-form-projects-scroll">
                  {projects.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500">
                      No projects. Create one first.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200/50">
                      {projects.map((p) => {
                        const checked = form.project_ids.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            className="flex items-center gap-3 cursor-pointer p-3"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() =>
                                setForm((f) => ({
                                  ...f,
                                  project_ids: checked
                                    ? f.project_ids.filter((x) => x !== p.id)
                                    : [...f.project_ids, p.id],
                                }))
                              }
                              className="rounded-xl"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium">{p.name}</div>
                              <div className="text-xs font-mono text-neutral-500">
                                {p.start_date} → {p.end_date}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-xl border-slate-300/70"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
                data-testid="client-submit"
              >
                {submitting ? "Saving…" : editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
